import { useMemo } from 'react'
import { useLocation, useParams, useSearchParams } from 'react-router-dom'

import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import ContentViewerDeletedFileState from '@/content-viewer/components/common/ContentViewerDeletedFileState'
import ContentViewerErrorState from '@/content-viewer/components/common/ContentViewerErrorState'
import { RenderFormat } from '@/content-viewer/store/schemas'
import { useTitle } from '@/hooks/use-title'
import { kPaxtonName } from '@/constants/constants-strings'
import UserFileContentRenderer from '@/user_files/files/components/UserFileContentRenderer'
import { useUserFile } from '@/user_files/files/hooks/useUserFiles'
import { buildUserFileRendererConfig } from '@/user_files/files/utils/build-user-file-renderer-config'

const EMPTY_PDF_HIGHLIGHTS: PaxtonHighlightArea[] = []
const EMPTY_MARKDOWN_HIGHLIGHTS: string[] = []

function parseStartingPage(value: string | null): number | undefined {
  if (value === null || !/^\d+$/.test(value)) return undefined
  return Number.parseInt(value, 10)
}

export default function StandaloneReferenceViewerUserFilePage() {
  const { matterId = '', userFileId = '' } = useParams<{ matterId: string; userFileId: string }>()
  const { hash } = useLocation()
  const excerpt = new URLSearchParams(hash.slice(1)).get('excerpt')
  const excerptHighlights = useMemo(() => (excerpt ? [excerpt] : EMPTY_MARKDOWN_HIGHLIGHTS), [excerpt])
  const [searchParams] = useSearchParams()
  const { file: loadedFile, loading, error } = useUserFile(userFileId || null)
  const file = loadedFile?.matter_id === matterId ? loadedFile : null
  const renderer = file ? buildUserFileRendererConfig(file) : null
  const startingPage = parseStartingPage(searchParams.get('page'))

  // The batch endpoint answers without the record when the file is gone or is no longer reachable by
  // this member, so an absent record on a request that did not fail is the deleted case rather than a
  // load failure. A record that resolved under a different matter is a bad link, not a deletion, so it
  // keeps the retryless error state below.
  const deleted = !loading && !error && !loadedFile

  const title = file?.original_filename ?? `${kPaxtonName} Reference Viewer`
  useTitle(title)

  const content =
    file && renderer ? (
      <UserFileContentRenderer
        userFileId={file.id}
        format={renderer.format}
        pdfHighlights={EMPTY_PDF_HIGHLIGHTS}
        markdownHighlights={excerptHighlights}
        pdfExcerpt={excerpt ?? undefined}
        startingPage={startingPage}
        title={file.original_filename}
      />
    ) : null

  return (
    <div className="grow flex flex-col h-full overflow-y-scroll pb-4 pt-8">
      {loading && (
        <div className="flex justify-center items-center flex-1">
          <CircularProgressContinuousSized size={25} thickness={5} />
        </div>
      )}

      {deleted && (
        <div className="pl-2">
          <ContentViewerDeletedFileState />
        </div>
      )}

      {!loading && !deleted && (error || !file || !renderer) && (
        <div className="pl-2">
          <ContentViewerErrorState message="Could not load this file." />
        </div>
      )}

      {file && renderer && !loading && !error && (
        <div className="w-full max-w-5xl mx-auto flex flex-col h-full min-h-0 gap-4">
          <div className="pl-2 flex items-center justify-between gap-4">
            <h1 className="text-xl font-bold leading-7 truncate text-brand-neutral-950">{file.original_filename}</h1>
          </div>

          {excerpt && (
            <blockquote className="px-4 py-2 text-sm border-l-2 border-brand-500 whitespace-pre-wrap" aria-label="Supporting excerpt">
              {excerpt}
            </blockquote>
          )}
          <div className="min-h-0 flex-1 bg-white border border-brand-neutral-200">
            {renderer.format === RenderFormat.MARKDOWN ? <div className="h-full w-full overflow-y-auto">{content}</div> : content}
          </div>
        </div>
      )}
    </div>
  )
}
